import { faUserCheck, faUserClock, faUserXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Grid, Typography, useTheme } from "@mui/material";
import React from "react";
import { AbsenceContainer } from "./styles";
import { QuestionIcon } from "../../../assets/icons";
import { CustomDate } from "../../../models/custom-date/custom-date";
import { TnAAbsenceDTO, TnAAbsenceRef } from "../../../models/tna";
import { LightTooltip } from "../../../reusable-components/CustomTooltip/CustomTooltip";
import { intlFormatDateMonth, intlFormatDateShort, intlFormatDateWithHour } from "../../../utils/time-utils";
import { PaidLeavesType } from "../utils";

interface Props {
  absence: TnAAbsenceDTO | TnAAbsenceRef;
  forManager?: boolean;
  paidLeaves?: PaidLeavesType;
  wrapContent?: boolean;
}

const AbsenceButton = ({ absence, forManager, paidLeaves, wrapContent }: Props) => {
  const theme = useTheme();
  return (
    <>
      <AbsenceContainer
        className="absence-container"
        wrapContent={wrapContent ?? false}
      >
        <LightTooltip
          title={
            <Grid sx={{ display: "flex", flexDirection: "column", maxWidth: "300px" }}>
              <Typography>
                {`${absence.absenceType?.name}: ${
                  absence.fromDate?.year !== absence.toDate?.year ||
                  absence.fromDate?.month !== absence.toDate?.month ||
                  absence.fromDate?.day !== absence.toDate?.day
                    ? `${intlFormatDateMonth(CustomDate.ToDate(absence.fromDate) as Date)} - ${intlFormatDateMonth(
                        CustomDate.ToDate(absence.toDate) as Date
                      )}`
                    : `${intlFormatDateMonth(CustomDate.ToDate(absence.fromDate) as Date)}`
                }`}
              </Typography>

              <Typography>
                Created by: <b>{absence.createdByUser?.name ?? absence.createdByUser?.email ?? ""}</b>
              </Typography>

              {absence.modifiedByUser && (absence.isApproved || absence.isRejected) && !absence.isCancelled && (
                <>
                  {absence.isApproved && (
                    <Typography>
                      Approved by:{" "}
                      <b>
                        {absence.modifiedByUser?.name ?? ""}:{" "}
                        {"modifiedAt" in absence && absence.modifiedAt
                          ? intlFormatDateWithHour(absence.modifiedAt)
                          : ""}
                      </b>
                    </Typography>
                  )}

                  {absence.isRejected && (
                    <Typography>
                      Rejected by:{" "}
                      <b>
                        {absence.modifiedByUser?.name ?? ""}:{" "}
                        {"modifiedAt" in absence && absence.modifiedAt
                          ? intlFormatDateWithHour(absence.modifiedAt)
                          : ""}
                      </b>
                    </Typography>
                  )}
                </>
              )}
            </Grid>
          }
        >
          <Typography
            sx={{
              textDecoration: absence.isRejected || absence.isCancelled ? "line-through" : "",
              lineHeight: "unset",
            }}
          >
            {`${absence.absenceType?.code}${
              !absence.isRejected && !absence.isCancelled && !absence.isApproved ? " ?" : ""
            }`}
          </Typography>
        </LightTooltip>

        <Grid style={{ display: "flex", gap: "5px" }}>
          {forManager && paidLeaves && !!paidLeaves[absence.id] && !!absence.countedDurationDays && "|"}
          {!!absence.countedDurationDays && (
            <Typography
              sx={{ fontSize: "10px" }}
              title={`Counted days :  ${absence.countedDurationDays} day${
                absence.countedDurationDays > 1 ? "s" : ""
              }/ Total absence days : ${absence.calendaristicDays} day${absence.calendaristicDays > 1 ? "s" : ""}`}
            >{` ${absence.countedDurationDays} day${absence.countedDurationDays > 1 ? "s" : ""} `}</Typography>
          )}

          <React.Fragment>
            {absence?.needAcceptance &&
              (absence?.acceptedByClient ? (
                <FontAwesomeIcon
                  icon={faUserCheck}
                  color={"greenyellow"}
                  title={`Accepted by client ${
                    absence.acceptedRejectedByUser?.name ?? absence.acceptedRejectedByEmail
                  }: ${absence.acceptedRejectedAt ? intlFormatDateWithHour(absence.acceptedRejectedAt) : ""}`}
                />
              ) : absence?.rejectedByClient ? (
                <FontAwesomeIcon
                  icon={faUserXmark}
                  color={theme.palette.error.main}
                  title={`Rejected by client ${
                    absence.acceptedRejectedByUser?.name ?? absence.acceptedRejectedByEmail
                  }: ${absence.acceptedRejectedAt ? intlFormatDateWithHour(absence.acceptedRejectedAt) : ""}`}
                />
              ) : (
                <FontAwesomeIcon
                  icon={faUserClock}
                  color={theme.palette.text.secondary}
                  title={"Pending client acceptance"}
                />
              ))}
          </React.Fragment>
          {!!absence.cancelRequested && !!absence.cancelRequestedAt && absence.cancellationRequestApproved === null && (
            <QuestionIcon
              style={{
                width: "20px",
                height: "20px",
                marginTop: "-5px",
                ...(wrapContent && {
                  position: "absolute",
                  right: "-4px",
                  top: "-5px",
                  marginTop: "unset",
                }),
              }}
              title={`Requested absence cancellation on ${intlFormatDateShort(new Date(absence.cancelRequestedAt))}`}
            />
          )}
        </Grid>
        {forManager && paidLeaves && !!paidLeaves[absence.id] && (
          <React.Fragment>
            <Typography
              sx={{ fontSize: "10px" }}
              title={`Paid leaves: ${paidLeaves[absence.id].toFixed(2)}`}
            >{`( ${paidLeaves[absence.id].toFixed(2)} )`}</Typography>
          </React.Fragment>
        )}
      </AbsenceContainer>
    </>
  );
};

export default AbsenceButton;
